import { useQuery } from '@apollo/client';
import { CreateNewFolder, CropOriginal, Sell } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDeleteContent, useMoveContent } from '~/api/content';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { ViewToggle } from '~/components/ViewToggle';
import { Count, CountSeparator } from '~/components/count';
import { MoveToFolderDialog } from '~/components/dialogs/MoveToFolderDialog';
import { ConfirmDialog } from '~/components/dialogs/confirmation';
import { FolderBreadcrumbs } from '~/components/folder-breadcrumbs';
import { SearchBar } from '~/components/forms/search-bar';
import { Toolbar } from '~/components/toolbar';
import { useAppContext, useFolderContext } from '~/contexts';
import { useUploadDispatch } from '~/contexts/upload';
import type { ContentOrderBy } from '~/generated/graphql';
import { useConfirmDialog } from '~/hooks/dialogs';
import { useLink } from '~/hooks/link';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { usePollInterval } from '~/hooks/polling';
import { assert } from '~/lib/assert';
import { ContentMoveDocument } from '../components';
import { ContentFolderViewDocument } from './ContentFolderView.generated';
import {
  AddFolderDialog,
  BulkActions,
  ContentAddButton,
  ContentBulkTagDialog,
  ContentDropTarget,
  ContentFilterAccordion,
  ContentFilterChips,
  ContentGrid,
  ContentPagination,
  ContentTable,
  ContentTagsDialog,
} from './components';
import { useCanDestroySelected, useFilterParams, useFilters } from './lib';

export const ContentFolderView = () => {
  const { t } = useTranslation();
  const params = useParams<{ contentFolderId: string }>();
  const contentFolderId = Number(params.contentFolderId);
  assert(isFinite(contentFolderId), 'contentFolderId param is not a legit number');

  const { currentNetwork } = useAppContext();
  const { folderTree } = useFolderContext();
  const location = useLocation();
  const navigate = useNavigate();
  const link = useLink();

  const [moveContentDialogOpen, setMoveContentDialogOpen] = useState(false);
  const [tagContentDialogOpen, setTagContentDialogOpen] = useState(false);
  const [moveContent, moveContentInfo] = useMoveContent();
  const [confirmDelete, confirmDeleteProps] = useConfirmDialog();
  const [deleteContent] = useDeleteContent();
  const [openNewFolderDialog, setOpenNewFolderDialog] = useState(false);
  const [showManageTags, setManageTags] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const viewParam = searchParams.get('view')?.trim();
  const view = viewParam === 'List' || viewParam === 'Grid' ? viewParam : 'Grid';

  const { pollInterval, setFastPolling } = usePollInterval(30_000, 1_000);

  const uploadDispatch = useUploadDispatch();

  const { kind, tags } = useFilterParams();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();
  const { orderBy, setOrderByParams, sortModel } = useOrderByParams<ContentOrderBy>();

  const { data: contentMoveData } = useQuery(ContentMoveDocument, {
    variables: { networkId: currentNetwork.id },
    skip: !moveContentDialogOpen,
  });
  const editableFolderIds = useMemo(
    () =>
      contentMoveData?.network?.contentFolders.filter((x) => x.canUpdate.value).map((x) => x.id) ??
      [],
    [contentMoveData],
  );

  const { data, loading, startPolling, stopPolling } = useQuery(ContentFolderViewDocument, {
    fetchPolicy: 'cache-and-network',
    variables: {
      condition: { kind, tags },
      contentFolderId,
      networkId: currentNetwork.id,
      ...(orderBy && { orderBy }),
      page,
      perPage,
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.contentFolder.content.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.contentFolder.content.pageInfo.nodesCount);

  const { clearFilter, filter, setFilterItems, toggleAccordion, togglePanel } = useFilters(
    data?.network?.contentTags.map((x) => x.name),
  );

  const filtersActive = useMemo(() => kind.length + tags.length > 0, [kind, tags]);

  useEffect(() => {
    startPolling(pollInterval);
    return () => stopPolling();
  }, [pollInterval, startPolling, stopPolling]);

  useEffect(() => {
    const listener = () => setFastPolling(30);
    uploadDispatch({ type: 'addListener', listener });
    return () => uploadDispatch({ type: 'removeListener', listener });
  }, [setFastPolling, uploadDispatch]);

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const canDestroySelected = useCanDestroySelected(selectedIds);

  const canUpload = data?.network?.contentFolder.canUpdate.value || false;

  const UploadButton = (
    <ContentAddButton
      canDesign={currentNetwork.canAccessStudio.value}
      canUpload={canUpload}
      onUpload={(returnFocus) => {
        uploadDispatch({
          type: 'setDialogOpen',
          dialogOpen: true,
          folderId: contentFolderId,
          returnFocus,
        });
      }}
    />
  );

  const handleDeleteContent = useCallback(async () => {
    if (!(await confirmDelete())) return;
    if (selectedIds.length === 0) return;
    await deleteContent({
      variables: {
        items: selectedIds.map((x) => {
          assert(typeof x === 'string');
          const parts = x.split('-');
          return {
            contentableId: Number(parts[1]),
            contentableType: parts[0] === 'ContentItem' ? 'APP' : 'MEDIA',
          };
        }),
      },
    });
    setSelectedIds([]);
  }, [confirmDelete, deleteContent, selectedIds]);

  const handleSearch = useCallback(
    (search: string) => {
      if (!search) return;
      navigate(`../search?search=${search}&backToFolder=${contentFolderId}`);
    },
    [contentFolderId, navigate],
  );

  if (!loading && !data?.network?.contentFolder)
    return <Navigate to={link('/not-found')} replace />;

  return (
    <>
      <Helmet title={t('pages.content.title')} />
      <Toolbar
        titleIcon={<CropOriginal />}
        titleText={t('pages.content.title')}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              disabled={!data?.network?.contentFolder.canUpdate.value}
              onClick={() => setOpenNewFolderDialog((prev: boolean) => !prev)}
              startIcon={<CreateNewFolder />}
              variant="outlined"
            >
              {t('pages.content.create_folder_button')}
            </Button>
            <Button
              onClick={() => setManageTags(!showManageTags)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('common.tags.manage_tags')}
            </Button>
            <span>{UploadButton}</span>
          </Box>
        }
        breadcrumbsLabel={
          data?.network?.contentFolder && (
            <FolderBreadcrumbs
              allItems={() =>
                navigate({ pathname: `../${folderTree.id}`, search: location.search })
              }
              label={t('pages.content.all_content_label')}
              changeFolder={(id) => navigate({ pathname: `../${id}`, search: location.search })}
              folder={data.network.contentFolder}
            />
          )
        }
      />

      <ListPageContainer>
        <FilterSidebar open={filter.panel.open} persistAs="contentFolder:filterWidth">
          <ContentFilterAccordion
            clearFilter={clearFilter}
            filters={filter.filters}
            filterCounts={data?.network?.contentFilterInfo}
            toggleAccordion={toggleAccordion}
            updateFilter={setFilterItems}
          />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle active={filtersActive} onToggle={togglePanel} />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.content.search_placeholder')}
                  search=""
                />
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions
                    canDestroySelected={canDestroySelected}
                    onDeleteContent={handleDeleteContent}
                    onMoveContent={() => setMoveContentDialogOpen(true)}
                    onTagContent={() => setTagContentDialogOpen(true)}
                  />
                ) : (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      paddingRight: 1,
                    }}
                  >
                    <Count
                      selectedCount={0}
                      totalCount={data?.network?.contentFolder.children.length || 0}
                      thing="common.pluralize.folder_plural"
                    />
                    <CountSeparator />
                  </Box>
                )}
                <Box sx={{ display: 'flex' }}>
                  <Count
                    selectedCount={selectedIds.length}
                    totalCount={totalCount}
                    thing="common.pluralize.item_plural"
                  />
                </Box>
              </Box>
            </ListActions>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                paddingBottom: 1,
              }}
            >
              <ContentFilterChips filters={filter.filters} updateFilter={setFilterItems} />
              <ViewToggle
                view={view}
                onViewChange={(newView) =>
                  setSearchParams((prev) => ({ ...Object.fromEntries(prev), view: newView }))
                }
              />
            </Box>
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {view === 'List' ? (
                  <ContentTable
                    items={data?.network?.contentFolder.content.nodes ?? []}
                    folders={data?.network?.contentFolder.children ?? []}
                    loading={loading}
                    onCheck={setSelectedIds}
                    onSortModelChange={setOrderByParams}
                    selectedIds={selectedIds}
                    sortModel={sortModel}
                  />
                ) : (
                  <ContentGrid
                    items={data?.network?.contentFolder.content.nodes ?? []}
                    folders={data?.network?.contentFolder.children ?? []}
                    loading={loading}
                    onCheck={setSelectedIds}
                    selectedIds={selectedIds}
                  />
                )}
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ContentPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>

          <MoveToFolderDialog
            editableFolderIds={editableFolderIds}
            onSubmit={async (targetFolderId) => {
              await moveContent({
                refetchQueries: ['ContentFolderView', 'ContentSearch'],
                variables: {
                  contentFolderId: targetFolderId,
                  items: (selectedIds as string[]).map((x) => {
                    const parts = x.split('-');
                    return {
                      contentableId: Number(parts[1]),
                      contentableType: parts[0] === 'ContentItem' ? 'APP' : 'MEDIA',
                    };
                  }),
                },
              });
              setSelectedIds([]);
            }}
            loading={moveContentInfo.loading}
            onClose={() => setMoveContentDialogOpen(false)}
            open={moveContentDialogOpen}
            i18nKey="pages.content.dialogs.move_content.title"
          />

          <AddFolderDialog
            contentFolderId={contentFolderId}
            openNewFolderDialog={openNewFolderDialog}
            setOpenNewFolderDialog={setOpenNewFolderDialog}
          />

          <ConfirmDialog
            {...confirmDeleteProps}
            title={t('pages.content.dialogs.delete_content.title')}
            prompt={t('pages.content.dialogs.delete_content.prompt')}
            confirm={t('common.dialogs.delete')}
            deleteConfirm
          />

          {showManageTags && (
            <ContentTagsDialog
              condition={{ kind, tags }}
              contentFolderId={contentFolderId}
              onClose={() => setManageTags(false)}
              open
              page={page}
              perPage={perPage}
            />
          )}

          {canUpload && <ContentDropTarget folderId={contentFolderId} />}

          {tagContentDialogOpen && (
            <ContentBulkTagDialog
              contentIds={selectedIds as string[]}
              contentTags={data?.network?.contentTags.map((x) => x.name) || []}
              onClose={() => setTagContentDialogOpen(false)}
              onComplete={() => setSelectedIds(() => [])}
              open
            />
          )}
        </ListContainer>
      </ListPageContainer>
    </>
  );
};
